import React, { useState, useEffect, useCallback } from 'react';
import {
  Row,
  Col,
  Card,
  Statistic,
  Table,
  Tag,
  Typography,
  Spin,
  DatePicker,
  Space,
  Progress,
  Badge,
  Divider,
  Empty,
  Alert,
} from 'antd';
import {
  UserOutlined,
  RiseOutlined,
  FallOutlined,
  TeamOutlined,
  FileTextOutlined,
  SearchOutlined,
  WarningOutlined,
  TrophyOutlined,
  DashboardOutlined,
  LineChartOutlined,
  PieChartOutlined,
  RocketOutlined,
  FundOutlined,
  BellOutlined,
  SyncOutlined,
  CalendarOutlined,
  ClockCircleOutlined,
  DatabaseOutlined,
  ExperimentOutlined,
} from '@ant-design/icons';
import { Line, Pie, Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title as ChartTitle,
  Tooltip as ChartTooltip,
  Legend,
  Filler,
} from 'chart.js';
import dayjs from 'dayjs';
import { api } from '../../api';
import './AdminDashboardV2.css';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  ChartTitle,
  ChartTooltip,
  Legend,
  Filler,
);

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

// 期間ラベルコンポーネント
const PeriodTag = ({ type, dateRange }) => {
  const configs = {
    realtime: {
      color: 'red',
      icon: <SyncOutlined spin />,
      label: 'リアルタイム',
    },
    period: {
      color: 'blue',
      icon: <CalendarOutlined />,
      label: dateRange
        ? `${dateRange[0].format('M/D')} - ${dateRange[1].format('M/D')}`
        : '選択期間',
    },
    fixed: {
      color: 'default',
      icon: <ClockCircleOutlined />,
      label: '固定期間',
    },
    cumulative: { color: 'purple', icon: <DatabaseOutlined />, label: '累計' },
  };
  const config = configs[type];
  return (
    <Tag color={config.color} className="period-tag">
      {config.icon} {config.label}
    </Tag>
  );
};

// 変化値表示コンポーネント
const ChangeIndicator = ({
  value,
  suffix = '',
  isPercentage = false,
  center = false,
}) => {
  if (value === 0 || value === undefined || value === null) return null;
  const displayValue = isPercentage
    ? `${value > 0 ? '+' : ''}${value}${suffix}`
    : `${value > 0 ? '+' : ''}${value.toLocaleString()}${suffix}`;
  return (
    <div style={{ textAlign: center ? 'center' : 'left' }}>
      <Text type={value > 0 ? 'success' : 'danger'} style={{ fontSize: 12 }}>
        {value > 0 ? <RiseOutlined /> : <FallOutlined />} {displayValue}
      </Text>
    </div>
  );
};

// セクションヘッダーコンポーネント
const SectionHeader = ({ icon, title }) => (
  <div className="section-header">
    <div className="section-title">
      {icon}
      <span>{title}</span>
    </div>
  </div>
);

const AdminDashboardV2 = () => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [dateRange, setDateRange] = useState([
    dayjs().startOf('month'),
    dayjs(),
  ]);

  useEffect(() => {
    const mainElement = document.querySelector('main.ant-layout-content');
    if (mainElement) mainElement.classList.add('admin-dashboard-v2');
    return () => {
      if (mainElement) mainElement.classList.remove('admin-dashboard-v2');
    };
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [start, end] = dateRange;
      const response = await api.get('/admin/dashboard/v2', {
        params: {
          start_date: start.format('YYYY-MM-DD'),
          end_date: end.format('YYYY-MM-DD'),
        },
      });
      setData(response.data);
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  }, [dateRange]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const rankDisplayNames = {
    white_1: 'ホワイト I',
    white_2: 'ホワイト II',
    white_3: 'ホワイト III',
    white_4: 'ホワイト IV',
    white_5: 'ホワイト V',
    bronze_1: 'ブロンズ I',
    bronze_2: 'ブロンズ II',
    bronze_3: 'ブロンズ III',
    silver_1: 'シルバー I',
    silver_2: 'シルバー II',
    silver_3: 'シルバー III',
    gold_1: 'ゴールド I',
    gold_2: 'ゴールド II',
    gold_3: 'ゴールド III',
    platinum_1: 'プラチナ I',
    platinum_2: 'プラチナ II',
    platinum_3: 'プラチナ III',
    diamond_1: 'ダイヤモンド I',
    diamond_2: 'ダイヤモンド II',
    diamond_3: 'ダイヤモンド III',
    legend: 'レジェンド',
  };

  // ========== 1. 指標サマリー ==========
  const renderSummaryCards = () => {
    const { summary } = data;
    return (
      <Row gutter={[16, 16]}>
        <Col xs={12} sm={8} lg={4}>
          <Card
            className="summary-card"
            extra={<PeriodTag type="period" dateRange={dateRange} />}
          >
            <Statistic
              title="アクティブユーザー"
              value={summary.active_users}
              prefix={<UserOutlined />}
            />
            <ChangeIndicator value={summary.active_users_change} center />
          </Card>
        </Col>
        <Col xs={12} sm={8} lg={4}>
          <Card
            className="summary-card"
            extra={<PeriodTag type="period" dateRange={dateRange} />}
          >
            <Statistic
              title="新規登録"
              value={summary.new_users}
              prefix={<TeamOutlined />}
            />
            <ChangeIndicator value={summary.new_users_change} center />
          </Card>
        </Col>
        <Col xs={12} sm={8} lg={4}>
          <Card
            className="summary-card"
            extra={<PeriodTag type="period" dateRange={dateRange} />}
          >
            <Statistic
              title="トライアル開始率"
              value={summary.trial_start_rate}
              suffix="%"
              precision={1}
              prefix={<ExperimentOutlined />}
            />
            <ChangeIndicator
              value={summary.trial_start_rate_change}
              suffix="pt"
              isPercentage
              center
            />
          </Card>
        </Col>
        {data?.trial_stats && (
          <Col xs={12} sm={8} lg={4}>
            <Card
              className="summary-card"
              extra={<PeriodTag type="period" dateRange={dateRange} />}
            >
              <Statistic
                title="トライアル転換率"
                value={data.trial_stats.conversion_rate}
                suffix="%"
                precision={1}
                valueStyle={{ color: data.trial_stats.conversion_rate >= 50 ? '#52c41a' : '#faad14' }}
              />
            </Card>
          </Col>
        )}
        <Col xs={12} sm={8} lg={4}>
          <Card
            className="summary-card"
            extra={<PeriodTag type="period" dateRange={dateRange} />}
          >
            <Statistic
              title="有料転換率"
              value={summary.conversion_rate}
              suffix="%"
              precision={1}
            />
            <ChangeIndicator
              value={summary.conversion_rate_change}
              suffix="pt"
              isPercentage
              center
            />
          </Card>
        </Col>
        <Col xs={12} sm={8} lg={4}>
          <Card
            className="summary-card"
            extra={<PeriodTag type="period" dateRange={dateRange} />}
          >
            <Statistic
              title="売上"
              value={summary.mrr}
              prefix="¥"
              formatter={v => v.toLocaleString()}
            />
            <ChangeIndicator value={summary.mrr_change} suffix="円" center />
          </Card>
        </Col>
        <Col xs={12} sm={8} lg={4}>
          <Card
            className="summary-card"
            extra={<PeriodTag type="period" dateRange={dateRange} />}
          >
            <Statistic
              title="チュートリアル完了率"
              value={summary.tutorial_completion_rate}
              suffix="%"
              precision={1}
            />
            <ChangeIndicator
              value={summary.tutorial_completion_rate_change}
              suffix="pt"
              isPercentage
              center
            />
          </Card>
        </Col>
        <Col xs={12} sm={8} lg={4}>
          <Card
            className="summary-card"
            extra={<PeriodTag type="period" dateRange={dateRange} />}
          >
            <Statistic
              title="平均EXP"
              value={summary.average_exp}
              prefix={<TrophyOutlined />}
              precision={1}
            />
            <ChangeIndicator value={summary.average_exp_change} center />
          </Card>
        </Col>
      </Row>
    );
  };

  // ========== 1.5 KPI移動平均グラフ ==========
  const renderMovingAverages = () => {
    const ma = data?.moving_averages;
    if (!ma) return null;

    // 共通のラベル（日付）を取得
    const labels =
      ma.registrations?.map(d => dayjs(d.date).format('M/D')) || [];

    // 実数トレンドグラフ（新規登録、アクティブ、有料転換）
    const countChartData = {
      labels,
      datasets: [
        {
          label: '新規登録',
          data: ma.registrations?.map(d => d.value) || [],
          borderColor: '#1890ff',
          backgroundColor: 'transparent',
          tension: 0.4,
          pointRadius: 2,
          pointHoverRadius: 5,
        },
        {
          label: 'アクティブ',
          data: ma.active_users?.map(d => d.value) || [],
          borderColor: '#52c41a',
          backgroundColor: 'transparent',
          tension: 0.4,
          pointRadius: 2,
          pointHoverRadius: 5,
        },
        {
          label: '有料転換',
          data: ma.paid_conversions?.map(d => d.value) || [],
          borderColor: '#722ed1',
          backgroundColor: 'transparent',
          tension: 0.4,
          pointRadius: 2,
          pointHoverRadius: 5,
        },
      ],
    };

    // 率トレンドグラフ（3回目ログイン率、有料転換率、継続率）
    const rateChartData = {
      labels,
      datasets: [
        {
          label: '3回目ログイン率',
          data: ma.third_login_rate?.map(d => d.value) || [],
          borderColor: '#fa8c16',
          backgroundColor: 'transparent',
          tension: 0.4,
          pointRadius: 2,
          pointHoverRadius: 5,
        },
        {
          label: '有料転換率',
          data: ma.conversion_rate?.map(d => d.value) || [],
          borderColor: '#eb2f96',
          backgroundColor: 'transparent',
          tension: 0.4,
          pointRadius: 2,
          pointHoverRadius: 5,
        },
        {
          label: 'プラン継続率',
          data: ma.retention_rate?.map(d => d.value) || [],
          borderColor: '#13c2c2',
          backgroundColor: 'transparent',
          tension: 0.4,
          pointRadius: 2,
          pointHoverRadius: 5,
        },
      ],
    };

    const countOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'top',
          labels: { boxWidth: 12, padding: 15 },
        },
      },
      scales: {
        y: { beginAtZero: true },
        x: { ticks: { maxRotation: 0 } },
      },
    };

    const rateOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'top',
          labels: { boxWidth: 12, padding: 15 },
        },
        tooltip: {
          callbacks: {
            label: ctx => `${ctx.dataset.label}: ${ctx.raw}%`,
          },
        },
      },
      scales: {
        y: {
          beginAtZero: true,
          max: 100,
          ticks: { callback: v => `${v}%` },
        },
        x: { ticks: { maxRotation: 0 } },
      },
    };

    return (
      <Row gutter={[16, 16]}>
        <Col xs={24} lg={12}>
          <Card
            size="small"
            title={
              <Space>
                <span>ユーザー数トレンド</span>
                <Tag color="blue" style={{ fontSize: 10 }}>
                  7日移動平均
                </Tag>
              </Space>
            }
            style={{ height: 320 }}
          >
            <div style={{ height: 250 }}>
              <Line data={countChartData} options={countOptions} />
            </div>
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card
            size="small"
            title={
              <Space>
                <span>率トレンド</span>
                <Tag color="blue" style={{ fontSize: 10 }}>
                  7日移動平均
                </Tag>
              </Space>
            }
            style={{ height: 320 }}
          >
            <div style={{ height: 250 }}>
              <Line data={rateChartData} options={rateOptions} />
            </div>
          </Card>
        </Col>
      </Row>
    );
  };

  // ========== 2. ユーザー利用動向 ==========
  const renderOnlineUsers = () => {
    const columns = [
      {
        title: 'ユーザー',
        key: 'user',
        render: (_, r) => (
          <div>
            <div style={{ fontSize: 12, color: '#999' }}>{r.company_name}</div>
            <div>{r.full_name}</div>
          </div>
        ),
      },
      { title: 'プラン', dataIndex: 'plan_name', key: 'plan_name', width: 100 },
      {
        title: 'ランク',
        dataIndex: 'current_rank',
        key: 'current_rank',
        width: 120,
        render: rank =>
          rank ? <Tag>{rankDisplayNames[rank] || rank}</Tag> : '-',
      },
      {
        title: 'EXP',
        dataIndex: 'total_exp',
        key: 'total_exp',
        width: 80,
        align: 'right',
      },
      {
        title: 'ログイン',
        dataIndex: 'last_login_at',
        key: 'last_login_at',
        width: 80,
        render: time => (time ? dayjs(time).format('HH:mm') : '-'),
      },
    ];
    return (
      <Card
        title={
          <Space>
            <Badge status="success" />
            <span>オンラインユーザー</span>
            <Tag color="green">{data.online_users.length}人</Tag>
          </Space>
        }
        extra={<PeriodTag type="realtime" />}
        className="dashboard-card"
      >
        {data.online_users.length > 0 ? (
          <Table
            dataSource={data.online_users}
            columns={columns}
            pagination={false}
            size="small"
            rowKey="user_id"
            scroll={{ y: 280 }}
          />
        ) : (
          <Empty description="現在オンラインのユーザーはいません" />
        )}
      </Card>
    );
  };

  const renderFunnel = () => {
    const { funnel } = data;
    if (!funnel || !funnel.steps)
      return (
        <Card
          title="ユーザーファネル"
          extra={<PeriodTag type="period" dateRange={dateRange} />}
          className="dashboard-card"
        >
          <Empty />
        </Card>
      );
    return (
      <Card
        title="ユーザーファネル"
        extra={<PeriodTag type="period" dateRange={dateRange} />}
        className="dashboard-card"
      >
        <div className="funnel-container">
          {funnel.steps.map((step, index) => (
            <div key={step.name} className="funnel-step">
              <div className="funnel-bar-container">
                <div
                  className="funnel-bar"
                  style={{ width: `${step.rate}%` }}
                />
                <div className="funnel-label">
                  <span className="funnel-name">{step.name}</span>
                  <span className="funnel-count">
                    {step.count}人 <ChangeIndicator value={step.change} />
                  </span>
                  <span className="funnel-rate">{step.rate}%</span>
                </div>
              </div>
              {index < funnel.steps.length - 1 && (
                <div className="funnel-drop">
                  <Text type="secondary" style={{ fontSize: 11 }}>
                    ↓{' '}
                    {(
                      funnel.steps[index].rate - funnel.steps[index + 1].rate
                    ).toFixed(1)}
                    %離脱
                  </Text>
                </div>
              )}
            </div>
          ))}
        </div>
      </Card>
    );
  };

  const renderActivityHeatmap = () => {
    const { activity_heatmap } = data;
    if (
      !activity_heatmap ||
      !activity_heatmap.data ||
      activity_heatmap.data.length === 0
    ) {
      return (
        <Card
          title="アクティビティヒートマップ"
          extra={<PeriodTag type="period" dateRange={dateRange} />}
          className="dashboard-card"
        >
          <Empty description="データがありません" />
        </Card>
      );
    }
    const days = ['日', '月', '火', '水', '木', '金', '土'];
    const hours = Array.from({ length: 24 }, (_, i) => i);
    const heatmapData = {};
    activity_heatmap.data.forEach(item => {
      heatmapData[`${item.day}-${item.hour}`] = item.count;
    });
    const maxCount = Math.max(...activity_heatmap.data.map(d => d.count), 1);
    return (
      <Card
        title="アクティビティヒートマップ"
        extra={<PeriodTag type="period" dateRange={dateRange} />}
        className="dashboard-card"
      >
        <div className="heatmap-wrapper">
          <div className="heatmap-container">
            <div className="heatmap-header">
              <div className="heatmap-day-label"></div>
              {hours.map(h => (
                <div key={h} className="heatmap-hour-label">
                  {h}
                </div>
              ))}
            </div>
            {days.map(day => (
              <div key={day} className="heatmap-row">
                <div className="heatmap-day-label">{day}</div>
                {hours.map(hour => {
                  const count = heatmapData[`${day}-${hour}`] || 0;
                  return (
                    <div
                      key={hour}
                      className="heatmap-cell"
                      style={{
                        backgroundColor: `rgba(24, 144, 255, ${(count / maxCount) * 0.8 + 0.1})`,
                      }}
                      title={`${day} ${hour}時: ${count}件`}
                    />
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      </Card>
    );
  };

  const renderRetention = () => {
    const { retention } = data;
    if (!retention || !retention.cohorts || retention.cohorts.length === 0) {
      return (
        <Card
          title="継続率（コホート分析）"
          extra={<PeriodTag type="fixed" />}
          className="dashboard-card"
        >
          <Empty description="データがありません" />
        </Card>
      );
    }
    const columns = [
      { title: '月', dataIndex: 'month', key: 'month' },
      { title: '登録数', dataIndex: 'total', key: 'total', align: 'right' },
      {
        title: 'Day 1',
        dataIndex: 'day_1',
        key: 'day_1',
        align: 'right',
        render: val => (val !== null ? `${val}%` : '-'),
      },
      {
        title: 'Day 7',
        dataIndex: 'day_7',
        key: 'day_7',
        align: 'right',
        render: val => (val !== null ? `${val}%` : '-'),
      },
      {
        title: 'Day 30',
        dataIndex: 'day_30',
        key: 'day_30',
        align: 'right',
        render: val => (val !== null ? `${val}%` : '-'),
      },
    ];
    return (
      <Card
        title="継続率（コホート分析・過去6ヶ月）"
        extra={<PeriodTag type="fixed" />}
        className="dashboard-card"
      >
        <Table
          dataSource={retention.cohorts}
          columns={columns}
          pagination={false}
          size="small"
          rowKey="month"
        />
      </Card>
    );
  };

  // ========== 3. ランク/プラン分布 ==========
  const renderRankDistribution = () => {
    const { rank_distribution } = data;
    if (!rank_distribution || !rank_distribution.months)
      return (
        <Card
          title="ランク分布推移（過去6ヶ月）"
          extra={<PeriodTag type="fixed" />}
          className="dashboard-card"
        >
          <Empty />
        </Card>
      );

    const chartData = {
      labels: rank_distribution.months,
      datasets: rank_distribution.datasets.map(ds => ({
        label: ds.label,
        data: ds.data,
        borderColor: ds.color,
        backgroundColor: ds.color + '20',
        tension: 0.3,
        pointRadius: 3,
        pointHoverRadius: 5,
      })),
    };

    const options = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'top',
          labels: { font: { size: 11 }, usePointStyle: true },
        },
      },
      scales: {
        y: { beginAtZero: true },
      },
    };

    return (
      <Card
        title="ランク分布推移（過去6ヶ月）"
        extra={<PeriodTag type="fixed" />}
        className="dashboard-card"
      >
        <div style={{ height: 280 }}>
          <Line data={chartData} options={options} />
        </div>
      </Card>
    );
  };

  const renderPlanDistribution = () => {
    const { plan_distribution } = data;
    if (!plan_distribution || !plan_distribution.months)
      return (
        <Card
          title="プラン分布推移（過去6ヶ月）"
          extra={<PeriodTag type="fixed" />}
          className="dashboard-card"
        >
          <Empty />
        </Card>
      );

    const chartData = {
      labels: plan_distribution.months,
      datasets: plan_distribution.datasets.map(ds => ({
        label: ds.label,
        data: ds.data,
        borderColor: ds.color,
        backgroundColor: ds.color + '20',
        tension: 0.3,
        pointRadius: 3,
        pointHoverRadius: 5,
      })),
    };

    const options = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'top',
          labels: { font: { size: 11 }, usePointStyle: true },
        },
      },
      scales: {
        y: { beginAtZero: true },
      },
    };

    return (
      <Card
        title="プラン分布推移（過去6ヶ月）"
        extra={<PeriodTag type="fixed" />}
        className="dashboard-card"
      >
        <div style={{ height: 280 }}>
          <Line data={chartData} options={options} />
        </div>
      </Card>
    );
  };

  // ========== 4. AIO利用状況 ==========
  const renderAIOUsage = () => {
    const { aio_usage } = data;
    return (
      <Card
        title="利用統計"
        extra={<PeriodTag type="period" dateRange={dateRange} />}
        className="dashboard-card"
      >
        <Row gutter={[16, 16]}>
          <Col span={12}>
            <Card size="small" className="inner-stat-card">
              <Statistic
                title="リサーチ数"
                value={aio_usage.research_count}
                prefix={<SearchOutlined />}
              />
              <ChangeIndicator value={aio_usage.research_change} center />
            </Card>
          </Col>
          <Col span={12}>
            <Card size="small" className="inner-stat-card">
              <Statistic
                title="生成数"
                value={aio_usage.generation_count}
                prefix={<FileTextOutlined />}
              />
              <ChangeIndicator value={aio_usage.generation_change} center />
            </Card>
          </Col>
          <Col span={12}>
            <Card size="small" className="inner-stat-card">
              <Statistic
                title="カスタムスタイル"
                value={aio_usage.custom_styles}
              />
              <ChangeIndicator value={aio_usage.custom_styles_change} center />
            </Card>
          </Col>
          <Col span={12}>
            <Card size="small" className="inner-stat-card">
              <Statistic title="独自情報" value={aio_usage.topping_data} />
              <ChangeIndicator value={aio_usage.topping_data_change} center />
            </Card>
          </Col>
        </Row>
      </Card>
    );
  };

  const renderKPITrend = () => {
    const { kpi_trend } = data;
    const chartData = {
      labels: kpi_trend.dates,
      datasets: [
        {
          label: '新規登録',
          data: kpi_trend.new_users,
          borderColor: '#52c41a',
          backgroundColor: 'rgba(82, 196, 26, 0.1)',
          fill: true,
          tension: 0.4,
        },
        {
          label: '有料転換',
          data: kpi_trend.paid_conversions,
          borderColor: '#1890ff',
          backgroundColor: 'rgba(24, 144, 255, 0.1)',
          fill: true,
          tension: 0.4,
        },
        {
          label: 'リサーチ数',
          data: kpi_trend.research_counts,
          borderColor: '#722ed1',
          backgroundColor: 'rgba(114, 46, 209, 0.1)',
          fill: true,
          tension: 0.4,
        },
        {
          label: '生成数',
          data: kpi_trend.generation_counts,
          borderColor: '#fa8c16',
          backgroundColor: 'rgba(250, 140, 22, 0.1)',
          fill: true,
          tension: 0.4,
        },
      ],
    };
    const options = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: { legend: { position: 'top' } },
      scales: { y: { beginAtZero: true } },
    };
    return (
      <Card
        title="KPI推移"
        extra={<PeriodTag type="period" dateRange={dateRange} />}
        className="dashboard-card"
      >
        <div style={{ height: 300 }}>
          <Line data={chartData} options={options} />
        </div>
      </Card>
    );
  };

  // ========== 5. マーケティング分析 ==========
  const renderUTMStats = () => {
    const { utm_stats } = data;
    const columns = [
      { title: 'ソース', dataIndex: 'source', key: 'source' },
      {
        title: '登録数',
        dataIndex: 'registrations',
        key: 'registrations',
        align: 'right',
      },
      {
        title: '有料転換',
        dataIndex: 'paid_conversions',
        key: 'paid_conversions',
        align: 'right',
      },
      {
        title: '転換率',
        dataIndex: 'conversion_rate',
        key: 'conversion_rate',
        align: 'right',
        render: val => `${val}%`,
      },
    ];
    return (
      <Card
        title="流入チャネル（UTM）"
        extra={<PeriodTag type="period" dateRange={dateRange} />}
        className="dashboard-card"
      >
        {utm_stats && utm_stats.length > 0 ? (
          <Table
            dataSource={utm_stats}
            columns={columns}
            pagination={false}
            size="small"
            rowKey="source"
          />
        ) : (
          <Empty description="UTMデータがありません" />
        )}
      </Card>
    );
  };

  const renderReferralStats = () => {
    const { referral_stats } = data;
    return (
      <Card
        title="紹介システム"
        extra={<PeriodTag type="period" dateRange={dateRange} />}
        className="dashboard-card"
      >
        <Row gutter={[16, 16]}>
          <Col span={8}>
            <div className="stat-center">
              <Statistic
                title="総紹介数"
                value={referral_stats.total_referrals}
              />
              <ChangeIndicator
                value={referral_stats.total_referrals_change}
                center
              />
            </div>
          </Col>
          <Col span={8}>
            <div className="stat-center">
              <Statistic
                title="成功数"
                value={referral_stats.completed_referrals}
                valueStyle={{ color: '#52c41a' }}
              />
              <ChangeIndicator
                value={referral_stats.completed_referrals_change}
                center
              />
            </div>
          </Col>
          <Col span={8}>
            <div className="stat-center">
              <Statistic
                title="成功率"
                value={referral_stats.success_rate}
                suffix="%"
              />
              <ChangeIndicator
                value={referral_stats.success_rate_change}
                suffix="pt"
                isPercentage
                center
              />
            </div>
          </Col>
        </Row>
        {referral_stats.top_referrers &&
          referral_stats.top_referrers.length > 0 && (
            <>
              <Divider style={{ margin: '16px 0' }} />
              <Text strong style={{ display: 'block', marginBottom: 8 }}>
                トップ紹介者
              </Text>
              <Table
                dataSource={referral_stats.top_referrers.slice(0, 5)}
                columns={[
                  {
                    title: 'ユーザー',
                    key: 'user',
                    render: (_, r) => (
                      <span>
                        {r.company_name || ''} {r.full_name}
                      </span>
                    ),
                  },
                  {
                    title: '紹介数',
                    dataIndex: 'referral_count',
                    key: 'referral_count',
                    align: 'right',
                  },
                  {
                    title: '成功',
                    dataIndex: 'completed_count',
                    key: 'completed_count',
                    align: 'right',
                  },
                ]}
                pagination={false}
                size="small"
                rowKey="user_id"
              />
            </>
          )}
      </Card>
    );
  };

  // ========== 6. 経営指標 ==========
  const renderMRRTrend = () => {
    const { mrr_trend } = data;
    if (!mrr_trend || !mrr_trend.dates || mrr_trend.dates.length === 0) {
      return (
        <Card
          title="MRR推移（過去6ヶ月）"
          extra={<PeriodTag type="fixed" />}
          className="dashboard-card"
        >
          <Empty />
        </Card>
      );
    }
    const chartData = {
      labels: mrr_trend.dates,
      datasets: [
        {
          label: 'MRR',
          data: mrr_trend.mrr,
          borderColor: '#1890ff',
          backgroundColor: 'rgba(24, 144, 255, 0.3)',
          fill: true,
        },
      ],
    };
    const options = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: { legend: { display: false } },
      scales: {
        y: {
          beginAtZero: true,
          ticks: { callback: v => `¥${v.toLocaleString()}` },
        },
      },
    };
    return (
      <Card
        title="MRR推移（過去6ヶ月）"
        extra={<PeriodTag type="fixed" />}
        className="dashboard-card"
      >
        <div style={{ height: 200 }}>
          <Bar data={chartData} options={options} />
        </div>
      </Card>
    );
  };

  const renderLTVMetrics = () => {
    const { ltv_metrics } = data;
    if (!ltv_metrics)
      return (
        <Card
          title="LTV指標"
          extra={<PeriodTag type="cumulative" />}
          className="dashboard-card"
        >
          <Empty />
        </Card>
      );
    return (
      <Card
        title="LTV指標"
        extra={<PeriodTag type="cumulative" />}
        className="dashboard-card"
      >
        <Row gutter={[16, 16]}>
          {data?.trial_stats && (
            <>
              <Col span={8}>
                <div className="stat-center">
                  <Statistic
                    title="トライアル中"
                    value={data.trial_stats.trialing_count}
                    prefix={<ExperimentOutlined />}
                    valueStyle={{ color: '#722ed1' }}
                  />
                </div>
              </Col>
              <Col span={8}>
                <div className="stat-center">
                  <Statistic
                    title="うちキャンセル予約"
                    value={data.trial_stats.trial_canceling_count || 0}
                    valueStyle={{ color: (data.trial_stats.trial_canceling_count || 0) > 0 ? '#ff4d4f' : '#999' }}
                    suffix="人"
                  />
                </div>
              </Col>
            </>
          )}
          <Col span={data?.trial_stats ? 8 : 12}>
            <div className="stat-center">
              <Statistic
                title="有料ユーザー数"
                value={ltv_metrics.paid_users}
              />
            </div>
          </Col>
          <Col span={8}>
            <div className="stat-center">
              <Statistic
                title="平均課金額"
                value={ltv_metrics.avg_revenue_per_user}
                prefix="¥"
                formatter={v => v.toLocaleString()}
              />
            </div>
          </Col>
          <Col span={8}>
            <div className="stat-center">
              <Statistic
                title="総売上"
                value={ltv_metrics.total_revenue}
                prefix="¥"
                formatter={v => v.toLocaleString()}
              />
            </div>
          </Col>
          <Col span={8}>
            <div className="stat-center">
              <Statistic
                title="チャーン率"
                value={ltv_metrics.churn_rate}
                suffix="%"
                valueStyle={{
                  color: ltv_metrics.churn_rate > 5 ? '#ff4d4f' : '#52c41a',
                }}
              />
            </div>
          </Col>
        </Row>
      </Card>
    );
  };

  // ========== 7. アラート ==========
  const renderChurnRiskUsers = () => {
    const { churn_risk_users } = data;
    const columns = [
      {
        title: 'ユーザー',
        key: 'user',
        render: (_, r) => (
          <div>
            <div style={{ fontSize: 12, color: '#999' }}>{r.company_name}</div>
            <div>{r.full_name}</div>
          </div>
        ),
      },
      { title: 'プラン', dataIndex: 'plan_name', key: 'plan_name', width: 100 },
      {
        title: '最終ログイン',
        dataIndex: 'last_login',
        key: 'last_login',
        width: 100,
        render: val => (val ? dayjs(val).format('MM/DD') : '未ログイン'),
      },
      {
        title: '非アクティブ',
        dataIndex: 'days_inactive',
        key: 'days_inactive',
        width: 80,
        render: val => (
          <Tag color={val > 60 ? 'red' : val > 30 ? 'orange' : 'default'}>
            {val}日
          </Tag>
        ),
      },
    ];
    return (
      <Card
        title={
          <Space>
            <WarningOutlined style={{ color: '#faad14' }} />
            <span>解約リスクユーザー</span>
          </Space>
        }
        extra={<PeriodTag type="realtime" />}
        className="dashboard-card alert-card-negative"
      >
        {churn_risk_users && churn_risk_users.length > 0 ? (
          <Table
            dataSource={churn_risk_users}
            columns={columns}
            pagination={false}
            size="small"
            rowKey="user_id"
            scroll={{ y: 220 }}
          />
        ) : (
          <Alert
            message="解約リスクの高いユーザーはいません"
            type="success"
            showIcon
          />
        )}
      </Card>
    );
  };

  const renderGrowthUsers = () => {
    const { growth_users } = data;
    const columns = [
      {
        title: 'ユーザー',
        key: 'user',
        render: (_, r) => (
          <div>
            <div style={{ fontSize: 12, color: '#999' }}>{r.company_name}</div>
            <div>{r.full_name}</div>
          </div>
        ),
      },
      {
        title: 'ランク',
        dataIndex: 'current_rank',
        key: 'current_rank',
        width: 100,
        render: rank =>
          rank ? <Tag>{rankDisplayNames[rank] || rank}</Tag> : '-',
      },
      {
        title: 'EXP獲得',
        dataIndex: 'exp_gained',
        key: 'exp_gained',
        width: 90,
        render: val => <Text type="success">+{val}</Text>,
      },
      {
        title: '紹介',
        dataIndex: 'referral_count',
        key: 'referral_count',
        width: 60,
      },
    ];
    return (
      <Card
        title={
          <Space>
            <RiseOutlined style={{ color: '#52c41a' }} />
            <span>成長ユーザー</span>
          </Space>
        }
        extra={<PeriodTag type="period" dateRange={dateRange} />}
        className="dashboard-card alert-card-positive"
      >
        {growth_users && growth_users.length > 0 ? (
          <Table
            dataSource={growth_users}
            columns={columns}
            pagination={false}
            size="small"
            rowKey="user_id"
            scroll={{ y: 220 }}
          />
        ) : (
          <Empty description="データがありません" />
        )}
      </Card>
    );
  };

  if (loading && !data)
    return (
      <div style={{ textAlign: 'center', padding: 100 }}>
        <Spin size="large" />
      </div>
    );
  if (!data) return <Empty description="データの取得に失敗しました" />;

  return (
    <div className="dashboard-content">
      <div className="dashboard-header">
        <Title level={3} className="dashboard-title">
          管理者ダッシュボード
        </Title>
        <RangePicker
          value={dateRange}
          onChange={setDateRange}
          format="YYYY/MM/DD"
          presets={[
            { label: '今月', value: [dayjs().startOf('month'), dayjs()] },
            {
              label: '先月',
              value: [
                dayjs().subtract(1, 'month').startOf('month'),
                dayjs().subtract(1, 'month').endOf('month'),
              ],
            },
            {
              label: '先々月',
              value: [
                dayjs().subtract(2, 'month').startOf('month'),
                dayjs().subtract(2, 'month').endOf('month'),
              ],
            },
            { label: '過去7日', value: [dayjs().subtract(7, 'day'), dayjs()] },
            {
              label: '過去30日',
              value: [dayjs().subtract(30, 'day'), dayjs()],
            },
            {
              label: '過去90日',
              value: [dayjs().subtract(90, 'day'), dayjs()],
            },
          ]}
        />
      </div>

      <Spin spinning={loading}>
        <section className="dashboard-section">
          <SectionHeader icon={<DashboardOutlined />} title="指標サマリー" />
          {renderSummaryCards()}
        </section>

        <section className="dashboard-section">
          <SectionHeader
            icon={<FundOutlined />}
            title="KPIトレンド（7日移動平均）"
          />
          {renderMovingAverages()}
        </section>

        <section className="dashboard-section">
          <SectionHeader
            icon={<LineChartOutlined />}
            title="ユーザー利用動向"
          />
          <Row gutter={[16, 16]}>
            <Col xs={24} lg={12}>
              {renderOnlineUsers()}
            </Col>
            <Col xs={24} lg={12}>
              {renderFunnel()}
            </Col>
          </Row>
          <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
            <Col xs={24} lg={12}>
              {renderActivityHeatmap()}
            </Col>
            <Col xs={24} lg={12}>
              {renderRetention()}
            </Col>
          </Row>
        </section>

        <section className="dashboard-section">
          <SectionHeader
            icon={<PieChartOutlined />}
            title="ランク/プラン分布"
          />
          <Row gutter={[16, 16]}>
            <Col xs={24} lg={12}>
              {renderRankDistribution()}
            </Col>
            <Col xs={24} lg={12}>
              {renderPlanDistribution()}
            </Col>
          </Row>
        </section>

        <section className="dashboard-section">
          <SectionHeader
            icon={<RocketOutlined />}
            title="AIOコンテンツモード利用状況"
          />
          <Row gutter={[16, 16]}>
            <Col xs={24} lg={8}>
              {renderAIOUsage()}
            </Col>
            <Col xs={24} lg={16}>
              {renderKPITrend()}
            </Col>
          </Row>
        </section>

        <section className="dashboard-section">
          <SectionHeader icon={<FundOutlined />} title="マーケティング分析" />
          <Row gutter={[16, 16]}>
            <Col xs={24} lg={12}>
              {renderUTMStats()}
            </Col>
            <Col xs={24} lg={12}>
              {renderReferralStats()}
            </Col>
          </Row>
        </section>

        <section className="dashboard-section">
          <SectionHeader icon={<FundOutlined />} title="経営指標" />
          <Row gutter={[16, 16]}>
            <Col xs={24} lg={12}>
              {renderMRRTrend()}
            </Col>
            <Col xs={24} lg={12}>
              {renderLTVMetrics()}
            </Col>
          </Row>
        </section>

        <section className="dashboard-section">
          <SectionHeader icon={<BellOutlined />} title="アラート" />
          <Row gutter={[16, 16]}>
            <Col xs={24} lg={12}>
              {renderChurnRiskUsers()}
            </Col>
            <Col xs={24} lg={12}>
              {renderGrowthUsers()}
            </Col>
          </Row>
        </section>
      </Spin>
    </div>
  );
};

export default AdminDashboardV2;
